<template>
	<div class="business-information">
		<header class="settings-header">
			<h2 class="section-title">Business Information</h2>
		</header>
		<div class="content-container">
			<div v-if="isLoading" class="loading-container">
				<div class="loading-spinner"></div>
				<p>Loading business information...</p>
			</div>

			<div v-else-if="error" class="error-container">
				<p class="error-message">{{ error }}</p>
			</div>

			<div v-else-if="!activeProject">
				<p class="error-message">No active project found. Please ensure your account has access to at least one project.</p>
			</div>

			<form v-else class="business-form" autocomplete="off">
				<div class="form-columns">
					<div class="form-column">
						<div class="form-group">
							<label for="businessName">Business Name</label>
							<input id="businessName" v-model="businessData.name" type="text" class="input" placeholder="Enter business name" />
						</div>

						<div class="form-group">
							<label for="businessAddress">Business Address</label>
							<textarea
								id="businessAddress"
								v-model="businessData.address"
								class="input textarea"
								placeholder="Enter full business address"
							></textarea>
						</div>

						<div class="form-group">
							<label>Business Logo</label>
							<div class="logo-upload-container">
								<div v-if="logoPreview" class="logo-preview">
									<img :src="logoPreview" alt="Business logo preview" />
									<button type="button" class="remove-logo" @click="removeLogo" :disabled="isSaving">
										<span aria-hidden="true">&times;</span>
										<span class="sr-only">Remove logo</span>
									</button>
								</div>
								<div v-else class="logo-upload">
									<label for="logoUpload" class="upload-label">
										<span class="upload-icon">⬆︎</span>
										<span>Upload logo</span>
									</label>
									<input
										id="logoUpload"
										ref="logoInputRef"
										class="file-input"
										type="file"
										accept="image/*"
										@change="handleLogoUpload"
										:disabled="isSaving"
									/>
								</div>
							</div>
							<p class="input-hint">Recommended: PNG or JPG, at least 300x300px.</p>
						</div>
					</div>

					<div class="form-column">
						<div class="form-row">
							<div class="form-group">
								<label for="businessEmail">Email</label>
								<input id="businessEmail" v-model="businessData.email" type="email" class="input" placeholder="Enter business email" />
							</div>

							<div class="form-group">
								<label for="businessPhone">Phone</label>
								<input id="businessPhone" v-model="businessData.phone" type="tel" class="input" placeholder="Enter business phone" />
							</div>
						</div>

						<div class="form-group">
							<label for="bankDetails">Bank Details</label>
							<textarea
								id="bankDetails"
								v-model="businessData.bankDetails"
								class="input textarea"
								placeholder="Enter bank details"
							></textarea>
						</div>

						<div class="form-row">
							<div class="form-group">
								<label for="registrationNumber">Company Registration Number</label>
								<input
									id="registrationNumber"
									v-model="businessData.regNumber"
									type="text"
									class="input"
									placeholder="Registration number"
								/>
							</div>

							<div class="form-group">
								<label for="vatNumber">VAT Number</label>
								<input id="vatNumber" v-model="businessData.vatNumber" type="text" class="input" placeholder="VAT number" />
							</div>
						</div>
					</div>
				</div>
			</form>
		</div>
	</div>
</template>

<script setup>
import {
	computed,
	getCurrentInstance,
	nextTick,
	onBeforeUnmount,
	onMounted,
	reactive,
	ref,
	toRaw,
	watch,
} from 'vue';
import { useProjectsStore } from '@/stores/projects';
import { useMediaStore } from '@/stores/media';

const AUTOSAVE_DELAY = 500;

const projectsStore = useProjectsStore();
const mediaStore = useMediaStore();
const { proxy } = getCurrentInstance();

const initialData = () => ({
	name: '',
	address: '',
	email: '',
	phone: '',
	regNumber: '',
	vatNumber: '',
	bankDetails: '',
	logo: null,
});

const businessData = reactive(initialData());
const logoPreview = ref(null);
const localLogoPreviewUrl = ref(null);
const isLoading = ref(false);
const error = ref(null);
const logoInputRef = ref(null);

const autosaveTimer = ref(null);
const autosaveState = ref('idle');
const formVersion = ref(0);
const lastSuccessfulSnapshot = ref(null);
const isSyncingBusinessData = ref(false);

const activeProject = computed(() => projectsStore.activeProject);
const activeProjectId = computed(() => activeProject.value?.id ?? null);
const hasActiveProject = computed(() => Boolean(activeProject.value));
const isSaving = computed(() => autosaveState.value === 'saving');

const assignBusinessData = (payload) => {
	Object.assign(businessData, initialData(), payload || {});
};

const clearLocalLogoPreview = () => {
	if (localLogoPreviewUrl.value) {
		URL.revokeObjectURL(localLogoPreviewUrl.value);
		localLogoPreviewUrl.value = null;
	}
};

const clearLogoPreview = () => {
	clearLocalLogoPreview();
	logoPreview.value = null;
};

const applyBusinessData = (payload = null, { capture = false } = {}) => {
	isSyncingBusinessData.value = true;
	assignBusinessData(payload || initialData());
	if (capture) {
		captureSnapshot();
	}
	nextTick(() => {
		isSyncingBusinessData.value = false;
	});
};

const fetchLogoPreview = async (logoId) => {
	if (!logoId) {
		clearLogoPreview();
		return;
	}
	try {
		clearLocalLogoPreview();
		const signed = await mediaStore.getSignedUrl(logoId);
		logoPreview.value = signed?.url || null;
	} catch (err) {
		logoPreview.value = null;
		proxy?.$logError?.(err, 'fetchLogoPreview');
	}
};

const loadBusinessData = async () => {
	clearAutosaveTimer();
	autosaveState.value = 'idle';
	if (!hasActiveProject.value) {
		error.value = null;
		applyBusinessData(null, { capture: true });
		clearLogoPreview();
		return;
	}
	isLoading.value = true;
	error.value = null;
	try {
		const project = activeProject.value;
		const settings = project.businessSettings || {};
		const cloned = { ...settings };
		if (cloned.lastSaved) {
			delete cloned.lastSaved;
		}
		if (project.logo?.id) {
			cloned.logo = project.logo;
			await fetchLogoPreview(project.logo.id);
		} else {
			cloned.logo = null;
			clearLogoPreview();
		}
		applyBusinessData(cloned, { capture: true });
		resetFileInput();
	} catch (err) {
		error.value = 'Failed to load business information.';
		proxy?.$logError?.(err, 'loadBusinessData');
	} finally {
		isLoading.value = false;
	}
};

const resetFileInput = () => {
	if (logoInputRef.value) {
		logoInputRef.value.value = '';
	}
};

const handleLogoUpload = (event) => {
	const [file] = event.target.files || [];
	if (!file) return;
	clearLocalLogoPreview();
	businessData.logo = file;
	const objectUrl = URL.createObjectURL(file);
	localLogoPreviewUrl.value = objectUrl;
	logoPreview.value = objectUrl;
};

const removeLogo = () => {
	businessData.logo = null;
	clearLogoPreview();
	resetFileInput();
};

const clearAutosaveTimer = () => {
	if (autosaveTimer.value) {
		clearTimeout(autosaveTimer.value);
		autosaveTimer.value = null;
	}
};

const canAutosave = () => {
	return hasActiveProject.value && !!activeProject.value?.documentId && !isLoading.value;
};

const scheduleAutosave = () => {
	if (!canAutosave()) return;
	clearAutosaveTimer();
	if (autosaveState.value === 'error') {
		autosaveState.value = 'idle';
	}
	autosaveTimer.value = setTimeout(() => {
		autosaveTimer.value = null;
		runAutosave();
	}, AUTOSAVE_DELAY);
};

const runAutosave = async () => {
	if (!canAutosave() || autosaveState.value === 'saving') return;
	const versionAtRequest = formVersion.value;
	const snapshotAtRequest = cloneBusinessData(businessData);
	const logoAtRequest = businessData.logo;
	autosaveState.value = 'saving';
	try {
		const result = await persistBusinessData();
		const hasPendingChanges = formVersion.value !== versionAtRequest;
		const savedSnapshot = snapshotAtRequest;
		savedSnapshot.logo = result?.logo || null;
		lastSuccessfulSnapshot.value = savedSnapshot;
		if (!hasPendingChanges) {
			await applyServerResult(result);
			resetFileInput();
		} else if (businessData.logo === logoAtRequest) {
			await syncLogoFromServer(result?.logo || null);
		}
		autosaveState.value = 'idle';
		if (hasPendingChanges) {
			scheduleAutosave();
		}
	} catch (err) {
		autosaveState.value = 'error';
		await restoreSnapshot();
		proxy?.$toast?.error?.('Failed to save business information');
		proxy?.$logError?.(err, 'saveBusinessData');
	}
};

const persistBusinessData = async () => {
	const project = activeProject.value;
	if (!project?.documentId) {
		throw new Error('Cannot save without an active project.');
	}
	const now = new Date();
	const payload = {
		name: businessData.name || '',
		address: businessData.address || '',
		email: businessData.email || '',
		phone: businessData.phone || '',
		regNumber: businessData.regNumber || '',
		vatNumber: businessData.vatNumber || '',
		bankDetails: businessData.bankDetails || '',
		lastSaved: now.toISOString(),
	};

	let logoId = null;
	const currentLogo = businessData.logo;
	const existingLogoId = project.logo?.id || null;

	if (currentLogo === null) {
		if (existingLogoId) {
			await mediaStore.deleteFile(existingLogoId);
		}
	} else if (currentLogo instanceof File) {
		if (existingLogoId) {
			await mediaStore.deleteFile(existingLogoId);
		}
		const uploaded = await mediaStore.uploadFile(currentLogo);
		logoId = uploaded?.id || null;
	} else if (currentLogo?.id) {
		logoId = currentLogo.id;
	}

	return projectsStore.updateProject({
		documentId: project.documentId,
		data: {
			businessSettings: payload,
			logo: logoId,
		},
	});
};

const applyServerResult = async (updatedProject) => {
	if (!updatedProject) return;
	const settings = { ...(updatedProject.businessSettings || {}) };
	if (settings.lastSaved) {
		delete settings.lastSaved;
	}
	settings.logo = updatedProject.logo || null;
	applyBusinessData(settings, { capture: true });
	if (updatedProject.logo?.id) {
		await fetchLogoPreview(updatedProject.logo.id);
	} else {
		clearLogoPreview();
	}
};

const syncLogoFromServer = async (logo) => {
	isSyncingBusinessData.value = true;
	businessData.logo = logo || null;
	nextTick(() => {
		isSyncingBusinessData.value = false;
	});
	if (logo?.id) {
		await fetchLogoPreview(logo.id);
	} else {
		clearLogoPreview();
	}
};

watch(
	() => activeProjectId.value,
	() => {
		loadBusinessData();
	}
);

watch(
	businessData,
	() => {
		if (isSyncingBusinessData.value) return;
		formVersion.value += 1;
		if (!canAutosave()) return;
		scheduleAutosave();
	},
	{ deep: true }
);

onMounted(() => {
	loadBusinessData();
});

onBeforeUnmount(() => {
	clearAutosaveTimer();
	clearLocalLogoPreview();
});

function captureSnapshot() {
	lastSuccessfulSnapshot.value = cloneBusinessData(businessData);
}

async function restoreSnapshot() {
	if (!lastSuccessfulSnapshot.value) return;
	isSyncingBusinessData.value = true;
	assignBusinessData(lastSuccessfulSnapshot.value);
	if (lastSuccessfulSnapshot.value.logo?.id) {
		await fetchLogoPreview(lastSuccessfulSnapshot.value.logo.id);
	} else {
		clearLogoPreview();
	}
	nextTick(() => {
		isSyncingBusinessData.value = false;
	});
}

function cloneBusinessData(source) {
	return structuredClone(deepUnref(source));
}

function deepUnref(value) {
	if (Array.isArray(value)) {
		return value.map((item) => deepUnref(item));
	}
	if (value && typeof value === 'object') {
		if (value instanceof File || value instanceof Blob || value instanceof Date) {
			return value;
		}
		const raw = toRaw(value);
		const result = Array.isArray(raw) ? [] : {};
		for (const key in raw) {
			result[key] = deepUnref(raw[key]);
		}
		return result;
	}
	return value;
}
</script>

<style scoped>
.business-information {
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 0;
	min-height: 0;
}


.settings-header {
	display: flex;
	align-items: baseline;
	margin-bottom: 8px;
	padding: 16px 24px 0 24px;
}

.content-container {
	width: 100%;
	flex: 1;
	padding: 0 24px;
	background: var(--noc-background);
	box-shadow: var(--box-shadow);
	min-height: 0;
	display: flex;
	flex-direction: column;
	gap: 16px;
}


.section-title {
	margin: 0;
	font-size: 1.25rem;
	font-weight: 600;
	letter-spacing: 0.02em;
}

.loading-container,
.error-container {
	background: var(--noc-background);
	border: 1px solid var(--border-color);
	border-radius: 12px;
	padding: 32px;
	text-align: center;
	box-shadow: var(--box-shadow);
}

.loading-spinner {
	width: 32px;
	height: 32px;
	margin: 0 auto 12px;
	border-radius: 50%;
	border: 3px solid var(--border-color);
	border-top-color: var(--noc-blue);
	animation: spin 1s linear infinite;
}

.error-message {
	color: var(--noc-red, #d9534f);
	text-align: center;
}

.business-form {
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: 20px;
	width: 100%;
}

.form-columns {
	display: flex;
	gap: 32px;
	width: 100%;
}

.form-column {
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 20px;
}

@media (max-width: 900px) {
	.business-form {
		width: 100%;
		min-width: 0;
	}

	.form-columns {
		flex-direction: column;
	}
}

.form-group {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.form-row {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
	gap: 16px;
}

label {
	font-size: 0.85rem;
	color: var(--text-color-secondary);
}

.input {
	width: 100%;
	border: 1px solid var(--border-color);
	border-radius: 4px;
	background: var(--noc-background);
	padding: 10px 12px;
	font-size: 0.9rem;
	color: var(--text-color);
	max-height: 40px;
	transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.textarea {
	min-height: 90px;
	resize: vertical;
	max-height: none;
}

.logo-upload-container {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.logo-preview {
	position: relative;
	width: 160px;
	height: 160px;
	border: 1px solid var(--border-color);
	border-radius: 12px;
	overflow: hidden;
}

.logo-preview img {
	width: 100%;
	height: 100%;
	object-fit: contain;
	background: var(--noc-background-secondary);
}

.remove-logo {
	position: absolute;
	top: 6px;
	right: 6px;
	border: none;
	border-radius: 999px;
	background: rgba(0, 0, 0, 0.6);
	color: #fff;
	width: 26px;
	height: 26px;
	cursor: pointer;
}

.logo-upload {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 160px;
	height: 160px;
	border: 1px dashed var(--border-color);
	border-radius: 12px;
	cursor: pointer;
	background: var(--noc-background-secondary);
}

.upload-label {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 6px;
	color: var(--text-color);
	cursor: pointer;
}

.upload-icon {
	font-size: 1.5rem;
}

.file-input {
	display: none;
}

.input-hint {
	font-size: 0.8rem;
	color: var(--text-color-secondary);
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

.sr-only {
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	border: 0;
}
</style>
